/**
 * Централизованная логика выполнения параметризованных команд
 *
 * Обрабатывает команды с parameters:
 * - set_filter - установить один фильтр
 * - set_filters - установить множественные фильтры
 * - view_cars - просмотр одного или нескольких авто
 * - clear_filters - очистка фильтров
 * - Навигационные команды (go_home, go_cars, go_about, go_contact, go_back_cars, go_back)
 * - Простые действия (scroll_top, scroll_bottom)
 */

import { diagnoseZeroResults } from './diagnoseZeroResults.js';
import { resolveWebsitePage, parseActiveFilters, CATALOG_PAGE_IDS } from '../utils/pageContextParser.js';
import { config } from '../config/runtimeConfig.js';
import { scrollWindowToTop, scrollWindowToBottom } from '../utils/scroll.js';
import { carTitle } from '../utils/carTitle';
import { deliveryCell } from '../utils/deliveryDate';
import { PINNED_CHAT_COMMANDS } from './commandGenerator.js';

// Импортируем browserControlWS для отправки событий агента
// Используем динамический импорт внутри методов, чтобы избежать циклических зависимостей
let browserControlWS;

/**
 * Widget types WidgetRenderer knows — the cases of its switch
 * (components/widgets/WidgetRenderer.jsx; a parity test keeps the two in step).
 * `show_widget` used to ACK success for ANY type, e.g. an invented `car_details`, and the
 * agent then told the customer the car was on screen while nothing appeared (#1411).
 */
export const SUPPORTED_WIDGET_TYPES = ['form', 'youtube', 'cards', 'gallery', 'comparison'];

const CATALOG_NOT_CONFIGURED_ERROR =
  'Catalog page is not configured for this site, so the car/filters cannot be opened from here.';

/**
 * Helper для поиска элементов внутри Shadow DOM
 * Виджет рендерится в Shadow DOM, поэтому querySelector не найдёт элементы
 * @param {string} selector - CSS селектор
 * @returns {Element|null} Найденный элемент или null
 */
function querySelector(selector) {
  // Сначала пробуем найти в Shadow DOM через B2BDemo API
  if (typeof window !== 'undefined' && window.B2BDemo) {
    // Ищем среди всех mounted containers
    const containers = document.querySelectorAll('[id^="b2b-demo"]');
    for (const container of containers) {
      if (container.shadowRoot) {
        const element = container.shadowRoot.querySelector(selector);
        if (element) {
          return element;
        }
      }
    }
  }

  // Fallback на обычный DOM (для standalone режима без Shadow DOM)
  return document.querySelector(selector);
}

export class CommandExecutor {
  constructor() {
    // Чат приколот к странице (страница чатов витрины): команды, уводящие с
    // неё, не исполняются, даже если агент их всё-таки прислал (#1330).
    // Нужен ВТОРОЙ рубеж помимо генератора: набор команд агенту заменяется
    // асинхронно, и ход, начатый до входа на страницу, держит прежний набор.
    this.pinned = false;

    // Навигационный стек для команды go_back (сохранение фильтров и состояния)
    // Восстанавливаем из sessionStorage чтобы история переживала перезагрузку страницы
    this.MAX_HISTORY = 10;
    this.navigationHistory = this._loadHistory();

    // Маппинг фильтров на URL параметры
    // Все фильтры работают через URL params, DOM элементы не нужны
    this.filterParamMap = {
      // Категориальные фильтры (один параметр)
      make: ['make'],
      model: ['model'],
      bodyStyle: ['bodyStyle'],
      fuelType: ['fuelType'],
      transmission: ['transmission'],
      // Range фильтры (два параметра: min и max)
      price: ['minPrice', 'maxPrice'],
      mileage: ['minMileage', 'maxMileage'],
      year: ['minYear', 'maxYear'],
      // Boolean toggle
      availableOnly: ['availableOnly'],
      // Скрытые фильтры для агента
      offerId: ['offerId'],
      offerIds: ['offerIds'],
      // Текст chip'а для offerId/offerIds выборки (не фильтр сам по себе,
      // но живёт в filterParamMap чтобы REPLACE-семантика и clear_filters
      // стирали его вместе с id-фильтром)
      offerIdsLabel: ['offerIdsLabel']
    };

    // Список категориальных фильтров (работают с массивом значений).
    // ВНИМАНИЕ: offerIds намеренно НЕ входит в этот список, хотя технически
    // тоже массив. Причина: diagnoseZeroResults.js читает c[filterType] из
    // инвентаря (у машин есть car.id, но нет car.offerIds) — попадание сюда
    // давало бы ложный VALUE_NOT_IN_INVENTORY для валидных ID. offerIds
    // обрабатывается отдельной веткой в setMultipleFilters и доступен
    // агенту только через set_filters (не через set_filter singular).
    this.categoricalFilters = ['make', 'model', 'bodyStyle', 'fuelType', 'transmission'];

    // Список range фильтров (работают с min/max)
    this.rangeFilters = ['price', 'mileage', 'year'];

    // Callback для добавления сообщений с виджетами (устанавливается в assistants)
    this.onAddMessage = null;

    // Issue #566: Current widget mode for navigation restore
    this.currentMode = 'chat'; // 'collapsed' | 'voice' | 'chat'

    // Issue #580: Agent speaking state (synced from VoiceWidgetConnected)
    this._agentSpeaking = false;

    // Issue #581: Text streaming state (synced from VoiceWidgetConnected). Nothing defers a
    // reload on it since #1485; kept so the widget's sync calls stay valid.
    this._textStreaming = false;
    this._proactiveStreaming = false;

    // #1485: the chat's messages typed during a turn and not sent yet (set by
    // VoiceWidgetConnected). A reload carries them to the restored page, which sends them.
    this._pendingMessagesProvider = null;
  }

  /**
   * #1485: Tell the executor where the chat keeps messages it has not sent yet.
   * @param {(() => string[]) | null} provider
   */
  setPendingMessagesProvider(provider) {
    this._pendingMessagesProvider = provider;
  }

  /**
   * #1485: The chat's unsent messages, for the restore data of a reload. The reload no longer
   * waits for the answer that holds them back, so without this they died with the page. Only
   * a chat restore sends them, so a reload that carries any restores into the chat — the
   * customer typed them a moment ago, and they get their answer there.
   * @returns {string[]}
   */
  _pendingMessages() {
    try {
      const pending = this._pendingMessagesProvider?.() || [];
      return pending.filter((text) => typeof text === 'string' && text.trim());
    } catch (error) {
      console.warn('[CommandExecutor] Could not read pending chat messages:', error);
      return [];
    }
  }

  /**
   * Issue #566: Set current widget mode (synced from VoiceWidgetConnected)
   * Used by navigateWebsite() to restore the correct mode after page reload
   * @param {string} mode - Current widget mode: 'collapsed' | 'voice' | 'chat'
   */
  setCurrentMode(mode) {
    this.currentMode = mode;
  }

  /**
   * Issue #580: Set agent speaking state (synced from VoiceWidgetConnected)
   * Used by navigateWebsite() to defer full-page navigation until agent finishes speaking
   * @param {boolean} isSpeaking - true when agent is actively speaking
   */
  setAgentSpeaking(isSpeaking) {
    this._agentSpeaking = isSpeaking;
  }

  /**
   * Issue #581: Set text streaming state (synced from VoiceWidgetConnected)
   * @param {boolean} isStreaming - true when text chat response is being streamed
   */
  setTextStreaming(isStreaming) {
    this._textStreaming = isStreaming;
  }

  /**
   * Issue #581: Set proactive streaming state (synced from VoiceWidgetConnected)
   * @param {boolean} isStreaming - true when proactive response is being streamed
   */
  setProactiveStreaming(isStreaming) {
    this._proactiveStreaming = isStreaming;
  }

  /**
   * Issue #580: Poll until agent stops speaking or timeout fires
   * Only used for go_website_page to prevent full page reload during speech.
   * go_website_page does window.location.href which kills LiveKit WebRTC connection.
   * @param {number} timeoutMs - Max wait time (default 8000ms)
   * @param {number} intervalMs - Poll interval (default 500ms)
   * @returns {Promise<string>} Reason: 'not_speaking' | 'speech_ended' | 'timeout' | 'not_voice_mode'
   */
  _waitForSpeakingEnd(timeoutMs = 8000, intervalMs = 500) {
    if (!this._agentSpeaking) return Promise.resolve('not_speaking');
    if (this.currentMode !== 'voice') return Promise.resolve('not_voice_mode');

    console.log(`[SpeakingGuard] Agent is speaking, deferring go_website_page (timeout: ${timeoutMs}ms)`);

    return new Promise((resolve) => {
      const startTime = Date.now();

      const checkInterval = setInterval(() => {
        // Agent stopped speaking
        if (!this._agentSpeaking) {
          clearInterval(checkInterval);
          console.log(`[SpeakingGuard] Agent stopped speaking after ${Date.now() - startTime}ms, proceeding`);
          resolve('speech_ended');
          return;
        }

        // Mode changed away from voice
        if (this.currentMode !== 'voice') {
          clearInterval(checkInterval);
          console.log(`[SpeakingGuard] Mode changed to "${this.currentMode}", proceeding`);
          resolve('not_voice_mode');
          return;
        }

        // Timeout
        if (Date.now() - startTime >= timeoutMs) {
          clearInterval(checkInterval);
          console.warn(`[SpeakingGuard] Timeout after ${timeoutMs}ms, proceeding with navigation`);
          resolve('timeout');
        }
      }, intervalMs);
    });
  }

  /**
   * Установить callback для добавления сообщений
   * @param {Function} callback - Функция для добавления сообщения в чат
   */
  setOnAddMessage(callback) {
    this.onAddMessage = callback;
  }

  /**
   * Оптимизированное чтение результатов фильтрации
   * Ждёт появления нового timestamp у window.currentFilteredCars (после применения фильтров)
   * или возвращает последнее состояние по тайм-ауту.
   * @returns {Promise<Object>} Promise с результатами
   */
  async getFilteredResultsOptimized({ timeoutMs = 500 } = {}) {
    const initialTimestamp = window.currentFilteredCars?.timestamp ?? 0;
    const startTime = Date.now();

    return new Promise(resolve => {
      const waitForFreshResults = () => {
        const currentData = window.currentFilteredCars;
        const currentTimestamp = currentData?.timestamp ?? 0;
        // Считаем данные свежими, когда timestamp обновился после запуска ожидания
        const isFresh =
          currentTimestamp &&
          (currentTimestamp > initialTimestamp || currentTimestamp >= startTime);

        if (isFresh) {
          resolve(this.getFilteredResults());
          return;
        }

        if (Date.now() - startTime >= timeoutMs) {
          console.warn(
            '[CommandExecutor] Timed out waiting for fresh filtered results, returning last known data'
          );
          resolve(this.getFilteredResults());
          return;
        }

        requestAnimationFrame(waitForFreshResults);
      };

      waitForFreshResults();
    });
  }

  /**
   * Получить текущий route из HashRouter
   * @returns {string} Текущий route (например, '/cars', '/', '/cars/123')
   */
  getCurrentRoute() {
    const hash = window.location.hash.substring(1); // Удаляем #
    const [pathname] = hash.split('?');
    return pathname || '/';
  }

  /**
   * Убедиться что мы на странице /cars, иначе перейти туда
   *
   * Issue #741: Проверяет не только client route, но и server page.
   * Off the catalog server page it returns an error naming the commands that work from there.
   * @returns {Promise<Object>} Результат проверки/навигации
   */
  async ensureOnCarsPage() {
    // Issue #741: Проверяем что мы на catalog server page
    const spaCheck = this._canUseSpaNavigation();
    if (!spaCheck.allowed) {
      // #1411: the hint lives in `error` — that is the only field the failure ACK relays to the
      // agent. It names what works off the catalog: go_website_page no longer targets the
      // catalog (#982) and go_cars is absent from the chat-only map.
      return {
        success: false,
        error: `GUARDRAIL_BLOCKED: this command only works on the car catalog page, and the customer is on "${spaCheck.currentPage}". From here use set_filters (applies the filters and opens the catalog in one step; an empty filters object opens the full catalog) or view_cars (opens one car).`
      };
    }

    const currentRoute = this.getCurrentRoute();

    // Уже на странице /cars
    if (currentRoute === '/cars') {
      return { success: true, action: 'already on /cars page' };
    }

    console.log(`[CommandExecutor] Not on /cars page (current: ${currentRoute}), navigating...`);

    // Сохраняем текущее состояние в навигационный стек для go_back (#734)
    this._pushHistory();

    // Переходим на /cars
    window.location.hash = '#/cars';

    // Ждем загрузки страницы (появления window.currentFilteredCars)
    const startTime = Date.now();
    const timeout = 3000; // 3 секунды максимум

    return new Promise((resolve) => {
      const checkPageLoaded = () => {
        // Проверяем что страница загрузилась (есть данные автомобилей)
        if (window.currentFilteredCars && window.currentFilteredCars.timestamp) {
          console.log(`[CommandExecutor] /cars page loaded successfully`);
          resolve({ success: true, action: `navigated to /cars from ${currentRoute}` });
          return;
        }

        // Timeout
        if (Date.now() - startTime >= timeout) {
          console.warn(`[CommandExecutor] Timeout waiting for /cars page to load`);
          resolve({
            success: false,
            error: 'Timeout waiting for /cars page to load',
            warning: 'Page may not be fully loaded'
          });
          return;
        }

        requestAnimationFrame(checkPageLoaded);
      };

      // Небольшая задержка перед началом проверки (дать React время на первый рендер)
      setTimeout(checkPageLoaded, 100);
    });
  }

  /**
   * Приколот ли чат к странице. Ставится из `App` (#1330).
   * @param {boolean} value
   */
  setPinned(value) {
    this.pinned = value === true;
  }

  /**
   * Выполнение команды
   * @param {string} commandId - ID команды
   * @param {Object} params - Параметры команды
   * @returns {Object} Результат выполнения { success, error?, action? }
   */
  async execute(commandId, params = {}) {
    console.log(`[CommandExecutor] Executing: ${commandId}`, params);

    if (this.pinned && !PINNED_CHAT_COMMANDS.has(commandId)) {
      console.warn(`[CommandExecutor] Refused (chat pinned to page): ${commandId}`);
      return {
        success: false,
        error: `Command ${commandId} is unavailable on this page: it would navigate away from the chat.`,
      };
    }

    // Устанавливаем флаг, чтобы подавить false-positive user-action события
    window.__agentCommandInProgress = true;

    try {
      let result;

      switch (commandId) {
        // ===== НАВИГАЦИЯ =====
        // Issue #741: navigateTo/goBack/goBackToCars are now async (may trigger full-page navigation)
        case 'go_home':
          result = await this.navigateTo('/');
          break;

        case 'go_cars':
          result = await this.navigateTo('/cars');
          break;

        case 'go_about':
          result = await this.navigateTo('/about');
          break;

        case 'go_contact':
          result = await this.navigateTo('/contact');
          break;

        case 'go_back_cars':
          result = await this.goBackToCars();
          break;

        case 'go_back':
          result = await this.goBack();
          break;

        case 'go_book_test_drive':
          result = await this.navigateToBookTestDrive(params);
          break;

        case 'go_service':
          result = await this.navigateTo('/service');
          break;

        case 'go_service_appointment':
          result = await this.navigateTo('/service/appointment');
          break;

        // ===== WEBSITE NAVIGATION (по сайту клиента) =====
        case 'go_website_page':
          result = await this.navigateWebsite(params);
          break;

        // ===== ОДИНОЧНЫЙ ФИЛЬТР =====
        case 'set_filter':
          result = await this.setSingleFilter(params);
          break;

        // ===== ПАКЕТНЫЕ ФИЛЬТРЫ =====
        case 'set_filters':
          result = await this.setMultipleFilters(params);
          break;

        // ===== ПРОСМОТР АВТО =====
        case 'view_cars':
          result = await this.viewCars(params);
          break;

        // ===== ОЧИСТКА ФИЛЬТРОВ =====
        case 'clear_filters':
          result = await this.clearFilters(params);
          break;

        // ===== TOGGLE AVAILABLE ONLY =====
        case 'toggle_available_only':
          result = await this.toggleAvailableOnly(params);
          break;

        // ===== СОРТИРОВКА =====
        case 'set_sort':
          result = this.setSort(params);
          break;

        // ===== ПРОСТЫЕ ДЕЙСТВИЯ =====
        case 'scroll_top':
          scrollWindowToTop();
          result = { success: true, action: 'scrolled to top' };
          break;

        case 'scroll_bottom':
          scrollWindowToBottom();
          result = { success: true, action: 'scrolled to bottom' };
          break;

        case 'notify_success_booking':
          result = this.notifySuccessBooking(params);
          break;

        // ===== ВИДЖЕТЫ =====
        case 'show_widget':
          result = await this.showWidget(params);
          break;

        default:
          console.warn(`[CommandExecutor] Unknown command: ${commandId}`);
          result = { success: false, error: `Unknown command: ${commandId}` };
      }

      // После выполнения команды отправляем событие агента
      if (result.success) {
        await this.sendAgentActionEvent(commandId, params, result);
      }

      return result;
    } catch (error) {
      console.error(`[CommandExecutor] Error executing ${commandId}:`, error);
      return { success: false, error: error.message };
    } finally {
      // Снимаем флаг с небольшой задержкой, чтобы React успел обработать изменения
      setTimeout(() => {
        // Флаг — браузерный глобал. Таймер переживает снос jsdom-окружения в
        // vitest и падает ReferenceError'ом в unhandled errors, роняя весь ран
        // при зелёных тестах (тот же класс, что был в runtimeConfig.js:1244).
        if (typeof window === 'undefined') return;
        window.__agentCommandInProgress = false;
      }, 150);
    }
  }

  /**
   * Навигация по route (с сохранением текущего состояния в навигационный стек)
   *
   * Issue #741: Smart routing — hash навигация работает только на catalog page.
   * На других серверных страницах автоматический fallback на full-page navigation.
   */
  async navigateTo(route) {
    const spaCheck = this._canUseSpaNavigation();

    if (spaCheck.allowed) {
      // Стандартный путь — hash навигация внутри каталога
      this._pushHistory();
      window.location.hash = `#${route}`;
      requestAnimationFrame(() => {
        scrollWindowToTop();
      });

      // Issue #741: Enriched response с контекстом каталога
      const context = this._buildCatalogContext();
      const navHistory = this._buildNavigationHistorySummary();
      const hint = this._generateNavigationHint('navigateTo', context, navHistory);

      return {
        success: true,
        action: `navigated to ${route}`,
        context,
        hint,
        navigationHistory: navHistory,
      };
    }

    // Issue #741: Не на catalog page — fallback на full-page navigation к каталогу
    // Intentional: все hash-роуты (/cars, /about, /contact и т.д.) живут внутри каталога,
    // поэтому при fallback всегда переходим на catalog page (запрошенный route игнорируется)
    console.warn(`[CommandExecutor] Not on catalog page (current: ${spaCheck.currentPage}), requested route "${route}" ignored — falling back to full-page navigation to catalog`);
    return this.navigateWebsite({ page: 'catalog' });
  }

  /**
   * Сохраняем текущее состояние (route + query params) в навигационный стек.
   * Стек персистится в sessionStorage чтобы переживать перезагрузку страницы (go_website_page).
   */
  _pushHistory() {
    const hash = window.location.hash.replace('#', '') || '/';
    const [route, search] = hash.split('?');
    this.navigationHistory.push({
      route,
      search: search ? `?${search}` : '',
      pathname: window.location.pathname, // Issue #741: сохраняем server page для smart go_back
      timestamp: Date.now()
    });
    if (this.navigationHistory.length > this.MAX_HISTORY) {
      this.navigationHistory.shift();
    }
    this._saveHistory();
  }

  /**
   * Извлекаем последнее состояние из навигационного стека
   */
  _popHistory() {
    const entry = this.navigationHistory.pop() || null;
    this._saveHistory();
    return entry;
  }

  /**
   * Сохраняем навигационный стек в sessionStorage
   */
  _saveHistory() {
    try {
      sessionStorage.setItem('sg_nav_history', JSON.stringify(this.navigationHistory));
    } catch (_) { /* ignore */ }
  }

  /**
   * Загружаем навигационный стек из sessionStorage.
   * Также проверяем localStorage для восстановления после full-page reload (Issue #741).
   */
  _loadHistory() {
    let history = [];

    // 1. Основной источник — sessionStorage (переживает hash-навигацию)
    try {
      const saved = sessionStorage.getItem('sg_nav_history');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          const valid = parsed.filter(e => e && typeof e.route === 'string');
          history = valid.slice(-this.MAX_HISTORY);
        }
      }
    } catch (_) { /* ignore */ }

    // 2. Issue #741: Восстановление из localStorage (переживает full-page reload)
    try {
      const persisted = localStorage.getItem('sg_nav_history_persist');
      if (persisted) {
        const data = JSON.parse(persisted);
        const TTL_MS = 5 * 60 * 1000; // 5 минут
        if (data && data.timestamp && (Date.now() - data.timestamp < TTL_MS)) {
          const persistedHistory = (data.history || []).filter(e => e && typeof e.route === 'string');
          if (persistedHistory.length > 0 && history.length === 0) {
            // Используем persisted history только если sessionStorage пустой
            // (значит был full-page reload)
            history = persistedHistory.slice(-this.MAX_HISTORY);
            console.log(`[CommandExecutor] Restored ${history.length} navigation history entries from localStorage (full-page reload recovery)`);
          }
        }
        // Одноразовое восстановление — удаляем после прочтения
        localStorage.removeItem('sg_nav_history_persist');
      }
    } catch (_) { /* ignore */ }

    return history;
  }

  /**
   * Issue #741: Persist навигационной истории в localStorage для выживания через full-page reload.
   * Вызывается перед navigateWebsite() (window.location.href).
   * TTL 5 минут — после этого история неактуальна.
   */
  _persistHistory() {
    try {
      const data = {
        history: this.navigationHistory.slice(-this.MAX_HISTORY),
        timestamp: Date.now(),
      };
      localStorage.setItem('sg_nav_history_persist', JSON.stringify(data));
      console.log(`[CommandExecutor] Persisted ${data.history.length} navigation history entries to localStorage`);
    } catch (_) { /* ignore */ }
  }

  /**
   * Проверка наличия истории навигации
   */
  canGoBack() {
    return this.navigationHistory.length > 0;
  }

  /**
   * Issue #741: Проверяет можно ли использовать hash-навигацию (SPA).
   * Hash-навигация работает только на странице каталога (catalog/used-cars).
   * Если WEBSITE_PAGES не настроены (standalone mode) — всегда разрешено.
   * @returns {{ allowed: boolean, currentPage: string|null }}
   */
  _canUseSpaNavigation() {
    const websitePage = resolveWebsitePage();
    // Если WEBSITE_PAGES не настроены — standalone mode, SPA всегда работает
    if (!websitePage) return { allowed: true, currentPage: null };

    // SPA hash navigation работает только на catalog page
    const catalogIds = CATALOG_PAGE_IDS;
    return {
      allowed: catalogIds.includes(websitePage.id),
      currentPage: websitePage.id,
    };
  }

  /**
   * Will this command do a FULL page reload (window.location.href) instead of in-place
   * SPA navigation? browserControlWebSocket.handleExecute uses this to ACK the command before
   * it runs (#581/#582), so the agent learns about the reload while this page can still send —
   * extended from go_website_page-only to the #982 off-catalog deep-link commands. Single
   * source of truth: the same _canUseSpaNavigation() decision used to pick deep-link vs
   * in-place. On the catalog these commands are synchronous and return real results the agent
   * needs, so they must NOT be early-ACKed there.
   * @param {string} commandId
   * @returns {boolean}
   */
  willTriggerReload(commandId) {
    if (commandId === 'go_website_page') return true;
    // #982: deep-link to the catalog (full reload) only when OFF the catalog SPA.
    // #1411: only these two deep-link. set_filter / clear_filters are catalog-only there
    // (ensureOnCarsPage refuses them), so an early ACK reported success for a command that
    // then did nothing — they now fail through the regular ACK, with a hint.
    const DEEP_LINK_COMMANDS = new Set(['view_cars', 'set_filters']);
    if (DEEP_LINK_COMMANDS.has(commandId)) {
      return !this._canUseSpaNavigation().allowed;
    }
    return false;
  }

  /**
   * Reject a reload command BEFORE browserControlWebSocket sends its early ACK (#1411).
   *
   * The early ACK reports success before the command runs (it has to — see
   * willTriggerReload), and the command's own result is then discarded. So whatever would
   * make it fail has to be caught here; otherwise a malformed call — set_filters without the
   * `filters` wrapper — is reported to the agent as done while nothing navigates.
   * Synchronous and side-effect-free; called only for commands willTriggerReload() accepted.
   *
   * @param {string} commandId
   * @param {Object} params
   * @returns {string|null} Error for the agent, or null when the command can run.
   */
  validateReloadCommand(commandId, params = {}) {
    const websitePages = config.WEBSITE_PAGES || [];

    if (commandId === 'go_website_page') {
      // Same lookup as navigateWebsite().
      const { page } = params;
      if (!page) return 'page parameter is required';
      const known = websitePages.some((p) => p.id === page || p.label === page);
      return known
        ? null
        : `Unknown page: ${page}. Available pages: ${websitePages.map((p) => p.id || p.label).join(', ')}`;
    }

    if (commandId !== 'view_cars' && commandId !== 'set_filters') return null;

    // _deepLinkToCatalog resolves its target from this entry.
    if (!websitePages.some((p) => CATALOG_PAGE_IDS.includes(p.id))) {
      return CATALOG_NOT_CONFIGURED_ERROR;
    }

    if (commandId === 'view_cars') {
      const { offerId } = params;
      return typeof offerId === 'string' && offerId.trim()
        ? null
        : 'view_cars needs params.offerId — the car\'s offerId from search_car_inventory, e.g. {"offerId": "1216"}';
    }

    const { filters } = params;
    if (!filters || typeof filters !== 'object' || Array.isArray(filters)) {
      const received = Object.keys(params).join(', ') || 'none';
      return `set_filters needs the filters inside params.filters, e.g. {"filters": {"make": ["BMW"], "availableOnly": true}} (params keys received: ${received})`;
    }
    const { appliedFilters, errors } = this._applyFiltersToParams(filters, new URLSearchParams());
    return errors.length > 0 && appliedFilters.length === 0 ? errors.join('; ') : null;
  }

  /**
   * The page a reload command lands on, for the early ACK (#1485). browserControlWebSocket ACKs
   * these commands before they run, so the command's own `targetPage` never reaches the agent;
   * without it the agent cannot tell when the new page's context has arrived. Same lookups as
   * navigateWebsite() and _deepLinkToCatalog().
   * @param {string} commandId
   * @param {Object} params
   * @returns {string|null} The target page id, or null when there is none to name.
   */
  reloadTargetPage(commandId, params = {}) {
    const websitePages = config.WEBSITE_PAGES || [];
    if (commandId === 'go_website_page') {
      const target = websitePages.find((p) => p.id === params.page || p.label === params.page);
      return target ? target.id : null;
    }
    if (commandId === 'view_cars' || commandId === 'set_filters') {
      const catalogPage = websitePages.find((p) => CATALOG_PAGE_IDS.includes(p.id));
      return catalogPage ? catalogPage.id : null;
    }
    return null;
  }

  /**
   * Write `filters` (the set_filters filter shape) into `urlParams` and report what was
   * applied / rejected. Touches nothing but `urlParams` — shared by setMultipleFilters and by
   * validateReloadCommand, which dry-runs it before the early ACK (#1411).
   * @param {Object} filters
   * @param {URLSearchParams} urlParams
   * @returns {{appliedFilters: string[], errors: string[]}}
   */
  _applyFiltersToParams(filters, urlParams) {
    const appliedFilters = [];
    const errors = [];

    Object.entries(filters).forEach(([filterType, value]) => {
      console.log(`[CommandExecutor] Processing filter: ${filterType}`, value);

      try {
        // Known filter type?
        if (!this.filterParamMap[filterType]) {
          console.warn(`[CommandExecutor] Unknown filter type: ${filterType}`);
          errors.push(`Unknown filter type: ${filterType}`);
          return;
        }

        // Categorical filters (array of values)
        if (Array.isArray(value) && this.categoricalFilters.includes(filterType)) {
          if (value.length > 0) {
            urlParams.set(filterType, value.join(','));
            appliedFilters.push(`${filterType}: ${value.join(', ')}`);
            console.log(`[CommandExecutor] Set categorical filter ${filterType}:`, value);
          }
        }
        // Range filters (min/max)
        else if (typeof value === 'object' && (value.min !== undefined || value.max !== undefined)) {
          if (this.rangeFilters.includes(filterType)) {
            const [minParam, maxParam] = this.filterParamMap[filterType];
            if (value.min !== undefined) {
              urlParams.set(minParam, value.min);
            }
            if (value.max !== undefined) {
              urlParams.set(maxParam, value.max);
            }
            appliedFilters.push(`${filterType}: ${value.min ?? 'any'} - ${value.max ?? 'any'}`);
            console.log(`[CommandExecutor] Set range filter ${filterType}:`, value);
          } else {
            console.warn(`[CommandExecutor] ${filterType} is not a range filter`);
            errors.push(`${filterType} is not a range filter`);
          }
        }
        // Boolean toggle (availableOnly)
        else if (typeof value === 'boolean' && filterType === 'availableOnly') {
          if (value) {
            urlParams.set('availableOnly', 'true');
          } else {
            urlParams.delete('availableOnly');
          }
          appliedFilters.push(`availableOnly: ${value}`);
          console.log(`[CommandExecutor] Set boolean filter ${filterType}:`, value);
        }
        // String filters (offerId) - hidden filter for agent use only
        else if (typeof value === 'string' && filterType === 'offerId') {
          urlParams.set(filterType, value);
          appliedFilters.push(`${filterType}: ${value}`);
          console.log(`[CommandExecutor] Set string filter ${filterType}:`, value);
        }
        // Multi-ID filter (offerIds) - hidden filter for agent use only.
        // Intentionally separate from categoricalFilters: diagnoseZeroResults
        // reads c[filterType] from inventory, but cars have c.id, not c.offerIds.
        else if (Array.isArray(value) && filterType === 'offerIds') {
          if (value.length > 0) {
            urlParams.set('offerIds', value.join(','));
            appliedFilters.push(`offerIds: ${value.join(', ')}`);
            console.log(`[CommandExecutor] Set multi-ID filter offerIds:`, value);
          }
        }
        // Selection chip text (offerIdsLabel) — accompanies offerId/offerIds.
        // Only written when the SAME call carries a non-empty id-filter:
        // the chip must never appear without the filter it describes.
        else if (typeof value === 'string' && filterType === 'offerIdsLabel') {
          const hasIdFilter = Boolean(filters.offerId) ||
            (Array.isArray(filters.offerIds) && filters.offerIds.length > 0);
          const label = value.trim().slice(0, 60);
          if (hasIdFilter && label) {
            urlParams.set('offerIdsLabel', label);
            appliedFilters.push(`offerIdsLabel: ${label}`);
            console.log(`[CommandExecutor] Set selection chip label:`, label);
          } else if (!hasIdFilter && label) {
            console.warn('[CommandExecutor] offerIdsLabel ignored: no offerId/offerIds in the same call');
          }
        }
        // Unknown value format
        else {
          console.warn(`[CommandExecutor] Invalid value format for ${filterType}:`, value);
          errors.push(`Invalid value format for ${filterType}`);
        }
      } catch (error) {
        console.error(`[CommandExecutor] Error applying ${filterType}:`, error);
        errors.push(`Error applying ${filterType}: ${error.message}`);
      }
    });

    return { appliedFilters, errors };
  }

  /**
   * Issue #741: Собрать динамический контекст каталога для enriched response.
   * Доступен только при hash-навигации (виджет жив, DOM доступен).
   * @returns {Object} Контекст текущей страницы каталога
   */
  _buildCatalogContext() {
    const hash = window.location.hash.replace('#', '') || '/';
    const [route, search] = hash.split('?');
    const searchParams = new URLSearchParams(search || '');
    const activeFilters = parseActiveFilters(searchParams);
    const activeFiltersCount = Object.keys(activeFilters).length;

    const filteredData = window.currentFilteredCars;
    const totalCars = filteredData?.cars?.length ?? null;
    const allCarsTotal = (typeof window !== 'undefined' && Array.isArray(window.allCars))
      ? window.allCars.length : null;

    return {
      currentPage: 'catalog',
      currentRoute: route || '/',
      activeFilters,
      activeFiltersCount,
      totalCars,
      allCarsTotal,
      availableOnly: searchParams.get('availableOnly') === 'true',
    };
  }

  /**
   * Issue #741: Собрать summary истории навигации для enriched response.
   * Помогает агенту восстановить контекст (какие фильтры были раньше).
   * @param {number} maxEntries - Макс. кол-во записей (default 3)
   * @returns {Array} Summary последних навигационных шагов
   */
  _buildNavigationHistorySummary(maxEntries = 3) {
    const history = this.navigationHistory.slice(-maxEntries);
    return history.map((entry, i) => {
      const result = {
        stepsAgo: history.length - i,
        route: entry.route,
      };

      // Server page info
      if (entry.pathname) {
        result.page = entry.pathname;
      }

      // Фильтры из search params
      if (entry.search && entry.search !== '') {
        result.filters = entry.search.replace('?', '');
      }

      return result;
    });
  }

  /**
   * Issue #741: Генерирует hint для агента после навигации.
   * @param {string} commandId - ID выполненной команды
   * @param {Object} context - Контекст текущей страницы
   * @param {Array} navHistory - Summary истории навигации
   * @returns {string} Подсказка для агента
   */
  _generateNavigationHint(commandId, context, navHistory) {
    if (!context) return '';

    if (context.currentPage === 'catalog') {
      const filtersInfo = context.activeFiltersCount > 0
        ? `Active filters: ${JSON.stringify(context.activeFilters)}.`
        : 'No filters applied.';
      const carsInfo = context.totalCars !== null
        ? ` ${context.totalCars} cars visible (${context.allCarsTotal} total).`
        : '';

      // Проверить в истории были ли фильтры
      const prevFilters = navHistory.find(h => h.filters && h.route === '/cars');
      const filterHint = (context.activeFiltersCount === 0 && prevFilters)
        ? ` User previously had filters (${prevFilters.filters}) — you may need to re-apply them.`
        : '';

      return `${filtersInfo}${carsInfo}${filterHint}`;
    }

    // Для non-catalog pages
    return context.note || '';
  }

  /**
   * Возврат на предыдущую страницу с восстановлением query params (фильтров)
   *
   * Issue #741: Smart go_back — проверяет pathname для определения типа навигации.
   * Если prev page на том же server pathname — hash nav.
   * Если pathname отличается — full-page navigation через navigateWebsite().
   */
  async goBack() {
    const prev = this._popHistory();
    if (!prev) {
      return { success: false, error: 'No navigation history available' };
    }

    // Issue #741: Проверяем pathname для smart навигации
    const currentPathname = window.location.pathname;
    const prevPathname = prev.pathname;

    // Если pathname совпадает (или не сохранён в старых записях) — hash навигация
    if (!prevPathname || prevPathname === currentPathname) {
      const fullPath = prev.search ? `${prev.route}${prev.search}` : prev.route;
      window.location.hash = `#${fullPath}`;
      requestAnimationFrame(() => {
        scrollWindowToTop();
      });

      // Issue #741: Enriched response
      const context = this._buildCatalogContext();
      const navHistory = this._buildNavigationHistorySummary();
      const hint = this._generateNavigationHint('go_back', context, navHistory);

      return {
        success: true,
        action: `navigated back to ${prev.route}`,
        previousPage: prev.route,
        context,
        hint,
        navigationHistory: navHistory,
      };
    }

    // Issue #741: Pathname изменился — нужна full-page navigation на предыдущую страницу
    console.log(`[CommandExecutor] go_back: pathname mismatch (current: ${currentPathname}, prev: ${prevPathname}), using full-page navigation`);

    // Ищем page ID в WEBSITE_PAGES по pathname
    const websitePages = config.WEBSITE_PAGES || [];
    const targetPage = websitePages.find(p => {
      const pagePath = (p.path || '').replace(/\/+$/, '');
      return pagePath && (prevPathname === pagePath || prevPathname.startsWith(pagePath + '/'));
    });

    if (targetPage) {
      return this.navigateWebsite({ page: targetPage.id });
    }

    // Fallback: прямой переход по pathname (для страниц не в WEBSITE_PAGES)
    console.log(`[CommandExecutor] go_back: page not found in WEBSITE_PAGES for pathname ${prevPathname}, direct navigation`);
    window.location.href = prevPathname;
    return {
      success: true,
      action: `navigated back to ${prevPathname}`,
    };
  }

  /**
   * Возврат на страницу /cars с сохранением фильтров из истории навигации.
   * Ищем в стеке последнее состояние /cars — если найдено, восстанавливаем с фильтрами.
   * Fallback: обычная навигация на /cars без фильтров.
   *
   * Issue #741: Smart routing — если не на catalog page, fallback на full-page navigation.
   */
  async goBackToCars() {
    const spaCheck = this._canUseSpaNavigation();

    if (!spaCheck.allowed) {
      // Issue #741: Не на catalog page — full-page navigation на каталог
      console.log(`[CommandExecutor] goBackToCars: not on catalog page (current: ${spaCheck.currentPage}), using full-page navigation`);
      return this.navigateWebsite({ page: 'catalog' });
    }

    // Ищем в истории последнее состояние /cars с фильтрами
    for (let i = this.navigationHistory.length - 1; i >= 0; i--) {
      if (this.navigationHistory[i].route === '/cars') {
        const entry = this.navigationHistory[i];
        // Удаляем всё из стека до этой записи включительно
        this.navigationHistory.splice(i);

        const fullPath = entry.search ? `/cars${entry.search}` : '/cars';
        window.location.hash = `#${fullPath}`;
        requestAnimationFrame(() => {
          scrollWindowToTop();
        });

        // Issue #741: Enriched response с контекстом
        // Ждём небольшой тик чтобы hash обновился, а пока собираем контекст из entry
        const restoredFilters = entry.search ? entry.search.replace('?', '') : '';
        const context = this._buildCatalogContext();
        const navHistory = this._buildNavigationHistorySummary();
        const hint = restoredFilters
          ? `Returned to car catalog with restored filters: ${restoredFilters}.`
          : 'Returned to car catalog with no filters.';

        return {
          success: true,
          action: 'navigated back to cars list with preserved filters',
          context,
          hint,
          navigationHistory: navHistory,
        };
      }
    }

    // Fallback: обычная навигация (как раньше)
    this._pushHistory();
    window.location.hash = '#/cars';
    requestAnimationFrame(() => {
      scrollWindowToTop();
    });

    // Issue #741: Enriched response
    const context = this._buildCatalogContext();
    const navHistory = this._buildNavigationHistorySummary();

    return {
      success: true,
      action: 'navigated to cars list',
      context,
      hint: 'Navigated to car catalog with no filters applied.',
      navigationHistory: navHistory,
    };
  }

  /**
   * Route, на котором HashRouter виджета в итоге остался — читается ПОСЛЕ того,
   * как у роутера был шанс выполнить свои редиректы (#1251).
   *
   * `window.location.hash = X` синхронен, но `<Navigate>` на wildcard-route
   * перезаписывает hash только в своём `useEffect` — т.е. после task'а
   * `hashchange` и последующего passive-effect flush'а React'а. Поэтому опрашиваем:
   * возвращаемся сразу, как только route ушёл с ожидаемого (snap-back пойман),
   * иначе — по истечении окна (route удержался, навигация настоящая).
   *
   * @param {string} expectedRoute - route, на который мы только что перешли
   * @param {number} [timeoutMs=150] - окно ожидания редиректа роутера
   * @returns {Promise<string>} Route, наблюдаемый в конце
   */
  _routeAfterRouterSettles(expectedRoute, timeoutMs = 150) {
    return new Promise((resolve) => {
      const startTime = Date.now();
      const check = () => {
        const route = this.getCurrentRoute();
        if (route !== expectedRoute || Date.now() - startTime >= timeoutMs) {
          resolve(route);
          return;
        }
        setTimeout(check, 16);
      };
      setTimeout(check, 0);
    });
  }

  /**
   * Навигация на страницу бронирования тест-драйва
   * Параметры: { carInfo?: string } - опциональная информация о машине в формате "year make model"
   */
  async navigateToBookTestDrive({ carInfo } = {}) {
    const route = '/book-test-drive';
    let url = route;

    if (carInfo) {
      // Добавляем query параметр car с информацией о машине
      url += `?car=${encodeURIComponent(carInfo)}`;
      console.log(`[CommandExecutor] Navigating to test drive booking for: ${carInfo}`);
    } else {
      console.log(`[CommandExecutor] Navigating to test drive booking (no car specified)`);
    }

    window.location.hash = `#${url}`;
    // Скроллим наверх после навигации (оптимизированно - одинарный RAF)
    requestAnimationFrame(() => {
      scrollWindowToTop();
    });

    // #1251: сам факт смены hash'а — НЕ доказательство что страница открылась.
    // Embed-шеллы виджета объявляют `<Route path="*">` с редиректом на `/cars`,
    // поэтому там hash отщёлкивает назад и покупатель никакой формы не видит.
    // Раньше метод синхронно возвращал success:true, и агент честно
    // пересказывал выданный ему успех («форма бронирования открыта на экране»).
    // Отчитываемся о провале вместо того, чтобы врать.
    const landedRoute = await this._routeAfterRouterSettles(route);
    if (landedRoute !== route) {
      console.warn(
        `[CommandExecutor] ${route} snapped back to ${landedRoute} — route does not exist in this mount`
      );
      return {
        success: false,
        error: `Route ${route} does not exist in this mount: the router redirected to ${landedRoute}.`,
        hint: 'No booking page was opened. Do NOT tell the customer a booking form is on their screen — collect the booking details in the conversation instead.',
      };
    }

    return {
      success: true,
      action: carInfo
        ? `navigated to test drive booking for ${carInfo}`
        : 'navigated to test drive booking page'
    };
  }

  /**
   * Навигация по сайту клиента (Website Navigation)
   * Используется для перехода на страницы дилера (контакты, услуги, о нас и т.д.)
   * В отличие от SPA навигации (navigateTo), здесь происходит полная перезагрузка страницы
   *
   * Параметры:
   * - page: string - ID страницы из websitePages конфигурации
   *
   * @param {Object} params - Параметры навигации
   * @returns {Promise<Object>} Результат навигации
   */
  async navigateWebsite({ page }) {
    const navStartTime = Date.now();
    console.log(`[CommandExecutor] navigateWebsite called:`, { page });

    if (!page) {
      return { success: false, error: 'page parameter is required' };
    }

    // Issue #580: Wait for agent to finish speaking before full page reload (voice mode).
    // go_website_page does window.location.href which kills LiveKit WebRTC connection,
    // so we defer until agent stops speaking to avoid cutting off speech mid-sentence.
    const speakingReason = await this._waitForSpeakingEnd();
    if (speakingReason !== 'not_speaking' && speakingReason !== 'not_voice_mode') {
      console.log(`[SpeakingGuard] go_website_page released after ${Date.now() - navStartTime}ms (reason: ${speakingReason})`);
    }

    // #1485: no wait for the text stream (#581 waited up to 8 s). The agent answers in the same
    // turn once the new page's context arrives, so that stream ends only after this reload, and
    // b2b-api hands what this page misses to the reloaded page. The plan phrase is already on
    // screen: the agent streams it before it runs the tool (#688).

    // Получаем конфигурацию страниц из runtimeConfig
    const { config } = await import('../config/runtimeConfig.js');
    const websitePages = config.WEBSITE_PAGES || [];

    // Ищем URL по page ID (#638: only page ID lookup, no direct URL param)
    // API entity uses: id, label, path, description
    const pageConfig = websitePages.find(p => p.id === page || p.label === page);
    if (!pageConfig) {
      console.error(`[CommandExecutor] Page not found in websitePages: ${page}`);
      return {
        success: false,
        error: `Unknown page: ${page}. Available pages: ${websitePages.map(p => p.id || p.label).join(', ')}`
      };
    }
    const targetUrl = pageConfig.path;

    // 1. Сохраняем данные для восстановления состояния виджета после навигации
    // Используем localStorage (не sessionStorage) т.к. работает между перезагрузками страницы
    // TTL 60 секунд - достаточно для загрузки новой страницы, но не слишком долго
    try {
      const pendingMessages = this._pendingMessages();
      const restoreData = {
        // Issue #566: Restore to current mode (voice or chat); #1485: carried messages need the chat
        mode: pendingMessages.length > 0 ? 'chat' : this.currentMode || 'chat',
        timestamp: Date.now(),  // Для проверки TTL
        targetPage: page, // Информация о целевой странице для логирования
        toolCycleContinue: true, // Issue #703: Flag для продолжения цикла агента после page reload
        ...(pendingMessages.length > 0 && { pendingMessages }), // #1485
      };
      localStorage.setItem('sg_restore_widget', JSON.stringify(restoreData));
      console.log(`[CommandExecutor] Saved widget restore data:`, restoreData);
    } catch (error) {
      console.warn(`[CommandExecutor] Could not save restore data to localStorage:`, error);
    }

    // 2. Формируем URL с query параметрами
    const targetUrlObj = new URL(targetUrl, window.location.origin);

    // 2a. Параметр активации виджета (для режима query-param)
    const activationParamName = config.ACTIVATION_QUERY_PARAM_NAME || 'shiftgears-enabled';
    const activationParamValue = config.ACTIVATION_QUERY_PARAM_VALUE || 'true';
    const activationMode = config.ACTIVATION_MODE || 'always';

    if (activationMode === 'query-param') {
      targetUrlObj.searchParams.set(activationParamName, activationParamValue);
    }

    // 2b. Query параметр для восстановления состояния виджета (fallback для cross-domain)
    // Это нужно если переход идёт на другой домен где localStorage недоступен
    targetUrlObj.searchParams.set('sg-restore', this.currentMode || 'chat');

    const finalUrl = targetUrlObj.toString();
    console.log(`[CommandExecutor] Navigating to website page: ${finalUrl}`);

    // 3. Сохраняем текущее состояние в навигационный стек для go_back (#734)
    // История персистится в sessionStorage, поэтому переживёт перезагрузку страницы
    this._pushHistory();

    // 4. Issue #741: Persist navigation history в localStorage для восстановления после reload
    this._persistHistory();

    // 5. Переходим на страницу (полная перезагрузка)
    window.location.href = finalUrl;

    // Issue #741: Enriched response со статическим контекстом из конфига
    const pageDescription = pageConfig.description || pageConfig.label || page;
    return {
      success: true,
      // #982/#1485: full page reload. willReload is the ground-truth signal: the agent waits for
      // the target page's context and answers in the same turn. Reaches the agent from the
      // early ACK (go_website_page) or the regular one (the navigateTo / go_back fallbacks).
      willReload: true,
      targetPage: pageConfig.id,
      action: `navigating to ${page}`,
      targetUrl: finalUrl,
      context: {
        targetPage: page,
        pageDescription,
      },
      hint: `Full page navigation to "${pageDescription}". Widget will reload. Catalog filters and state are reset.`,
      navigationHistory: this._buildNavigationHistorySummary(),
    };
  }

  /**
   * #982: Deep-link straight to a catalog hash route from ANY page in one navigation.
   *
   * Used by view_cars / set_filters when the user is NOT on the catalog page — instead of
   * the old 2-step (go_cars/go_website_page → then the catalog command, which costs an extra
   * LLM turn + a post-reload context rebuild) we do a single full-page navigation directly to
   * `{catalogPath}#{hashPath}` (e.g. "/cars/1216" or "/cars?make=BMW"). The catalog SPA
   * initialises from the hash on load (detail route / applied filters). Reuses the same
   * widget-restore + speaking guard as navigateWebsite() so a voice turn isn't cut off
   * mid-sentence; a text answer is handed to the reloaded page by b2b-api (#1485).
   *
   * @param {string} hashPath - HashRouter path incl. optional query, WITHOUT leading '#'
   *   (e.g. "/cars/1216", "/cars?make=BMW&availableOnly=true")
   * @returns {Promise<Object>} Immediate result. A full reload follows; the agent answers once
   *   the catalog page's context arrives, and the answer reaches the reloaded page (#1485).
   */
  async _deepLinkToCatalog(hashPath) {
    // Defer the reload until the agent finishes speaking (#580) — window.location.href kills
    // the LiveKit connection. No wait for the text stream: see navigateWebsite() (#1485).
    await this._waitForSpeakingEnd();

    const websitePages = config.WEBSITE_PAGES || [];
    const catalogPage = websitePages.find(p => CATALOG_PAGE_IDS.includes(p.id));

    // We only reach _deepLinkToCatalog when OFF the catalog (see callers). If WEBSITE_PAGES
    // has no catalog page, we cannot deep-link — setting the hash on a non-catalog page would
    // NOT render the catalog. Fail loudly instead of a misleading silent success.
    if (!catalogPage) {
      console.warn('[CommandExecutor] _deepLinkToCatalog: no catalog page in WEBSITE_PAGES — cannot open', hashPath);
      return {
        success: false,
        error: CATALOG_NOT_CONFIGURED_ERROR,
      };
    }

    // Build {origin}{catalogPath}?{activation+restore params}#{hashPath}.
    // Widget activation / restore params are real query params (before '#'); the catalog
    // route + filters live in the HashRouter fragment (after '#').
    const targetUrlObj = new URL(catalogPage.path, window.location.origin);
    const activationMode = config.ACTIVATION_MODE || 'always';
    if (activationMode === 'query-param') {
      const name = config.ACTIVATION_QUERY_PARAM_NAME || 'shiftgears-enabled';
      const value = config.ACTIVATION_QUERY_PARAM_VALUE || 'true';
      targetUrlObj.searchParams.set(name, value);
    }
    targetUrlObj.searchParams.set('sg-restore', this.currentMode || 'chat');
    const finalUrl = `${targetUrlObj.toString()}#${hashPath}`;

    // Persist widget state across the full reload (mode + continue the agent tool cycle).
    try {
      const pendingMessages = this._pendingMessages();
      localStorage.setItem('sg_restore_widget', JSON.stringify({
        mode: pendingMessages.length > 0 ? 'chat' : this.currentMode || 'chat', // #1485: see _pendingMessages
        timestamp: Date.now(),
        targetPage: catalogPage.id,
        toolCycleContinue: true, // #703: resume the agent loop after reconnect
        ...(pendingMessages.length > 0 && { pendingMessages }), // #1485
      }));
    } catch (error) {
      console.warn('[CommandExecutor] Could not save restore data for deep-link:', error);
    }

    this._pushHistory();
    this._persistHistory();

    console.log(`[CommandExecutor] Deep-linking to catalog: ${finalUrl}`);
    window.location.href = finalUrl;

    return {
      success: true,
      // #982/#1485: full page reload — see navigateWebsite().
      willReload: true,
      targetPage: catalogPage.id, // #725: the agent waits for this page's context
      action: `opening ${hashPath} on the catalog (one-step deep-link)`,
      targetUrl: finalUrl,
      hint: `Single navigation directly to ${hashPath}. Widget reloads; on-screen results are available after reconnect.`,
      navigationHistory: this._buildNavigationHistorySummary(),
    };
  }

  /**
   * Установка одного фильтра
   * Все фильтры работают через URL params (DOM элементы не нужны)
   * Параметры: { filterType, values?, min?, max? }
   */
  async setSingleFilter({ filterType, values, min, max }) {
    if (!filterType) {
      return { success: false, error: 'filterType is required' };
    }

    // Проверяем что это известный тип фильтра
    if (!this.filterParamMap[filterType]) {
      return { success: false, error: `Unknown filter type: ${filterType}` };
    }

    // Убедиться что мы на странице /cars
    const navigationResult = await this.ensureOnCarsPage();
    if (!navigationResult.success) {
      return navigationResult;
    }

    // Получаем текущие URL параметры
    const hash = window.location.hash;
    const [path, queryString] = hash.split('?');
    const urlParams = new URLSearchParams(queryString || '');

    // Issue #661: REPLACE semantics — clear all filter params before applying new ones.
    // set_filter represents the COMPLETE desired filter state, not a delta.
    Object.values(this.filterParamMap).flat().forEach(param => urlParams.delete(param));

    let actionDescription = '';

    // Категориальные фильтры (массив значений)
    if (values && this.categoricalFilters.includes(filterType)) {
      if (values.length > 0) {
        urlParams.set(filterType, values.join(','));
        actionDescription = `applied ${filterType} filter with values: ${values.join(', ')}`;
      } else {
        urlParams.delete(filterType);
        actionDescription = `cleared ${filterType} filter`;
      }
      console.log(`[CommandExecutor] Setting categorical filter ${filterType}:`, values);
    }
    // Range фильтры (min/max)
    else if ((min !== undefined || max !== undefined) && this.rangeFilters.includes(filterType)) {
      const [minParam, maxParam] = this.filterParamMap[filterType];
      if (min !== undefined) {
        urlParams.set(minParam, min);
      }
      if (max !== undefined) {
        urlParams.set(maxParam, max);
      }
      actionDescription = `applied ${filterType} range filter (min: ${min ?? 'any'}, max: ${max ?? 'any'})`;
      console.log(`[CommandExecutor] Setting range filter ${filterType}:`, { min, max });
    }
    // Ошибка валидации
    else {
      return { success: false, error: 'Either values (for categorical) or min/max (for range) must be provided' };
    }

    // Обновляем URL
    const newHash = urlParams.toString() ? `${path}?${urlParams.toString()}` : path;
    console.log(`[CommandExecutor] Updating URL hash: ${newHash}`);
    window.location.hash = newHash;

    // Читаем результаты после применения фильтра
    const results = await this.getFilteredResultsOptimized();

    // Issue #714: When 0 results, diagnose using ALL active filters from URL
    if (results.total === 0 && results.allCarsTotal > 0) {
      const allActiveFilters = this._parseActiveFilters(urlParams);
      results.diagnosis = this._diagnoseZeroResults(allActiveFilters);
    }

    return {
      success: true,
      action: actionDescription,
      results
    };
  }

  /**
   * Установка множественных фильтров
   * Все фильтры работают через URL params (DOM элементы не нужны)
   * Параметры: { filters: { make: [...], price: { min, max }, ... } }
   */
  async setMultipleFilters({ filters }) {
    console.log('[CommandExecutor] setMultipleFilters called with:', filters);

    if (!filters || typeof filters !== 'object') {
      return { success: false, error: 'filters object is required' };
    }

    // #982: Decide the page up-front. Off-catalog we DON'T error out (old behaviour) — we
    // build the desired filter state and deep-link straight to /cars?<params> in one
    // navigation. REPLACE semantics means building fresh == clearing-then-setting on-catalog.
    const spaCheck = this._canUseSpaNavigation();
    const offCatalog = !spaCheck.allowed;

    let path = '/cars';
    let urlParams;
    if (offCatalog) {
      urlParams = new URLSearchParams();
    } else {
      const navigationResult = await this.ensureOnCarsPage();
      if (!navigationResult.success) {
        return navigationResult;
      }
      const hash = window.location.hash;
      const [currentPath, queryString] = hash.split('?');
      path = currentPath || '/cars';
      urlParams = new URLSearchParams(queryString || '');
      // Issue #661: REPLACE semantics — clear all filter params before applying new ones.
      // set_filters represents the COMPLETE desired filter state, not a delta.
      Object.values(this.filterParamMap).flat().forEach(param => urlParams.delete(param));
    }

    const { appliedFilters, errors } = this._applyFiltersToParams(filters, urlParams);

    // #982: Off-catalog → one-step deep-link to the catalog with these filters applied.
    // Results aren't available synchronously (full reload); the agent reads the grid after
    // the widget reconnects and the tool cycle continues (toolCycleContinue).
    if (offCatalog) {
      if (errors.length > 0 && appliedFilters.length === 0) {
        return { success: false, error: errors.join('; ') };
      }
      const qs = urlParams.toString();
      const hashPath = qs ? `/cars?${qs}` : '/cars';
      const deepLink = await this._deepLinkToCatalog(hashPath);
      return {
        ...deepLink,
        action: appliedFilters.length > 0
          ? `opening catalog with filters: ${appliedFilters.join('; ')} (one-step deep-link)`
          : deepLink.action,
        error: errors.length > 0 ? errors.join('; ') : undefined,
      };
    }

    // Batch обновление URL - делаем один раз в конце
    const newHash = urlParams.toString() ? `${path}?${urlParams.toString()}` : path;
    console.log(`[CommandExecutor] Batch updating URL hash: ${newHash}`);
    window.location.hash = newHash;

    // Читаем результаты после изменения URL
    const results = await this.getFilteredResultsOptimized();

    // Issue #714: When 0 results, diagnose why and attach to results
    if (results.total === 0 && results.allCarsTotal > 0) {
      results.diagnosis = this._diagnoseZeroResults(filters);
    }

    // Issue #523: Всегда возвращаем results даже при ошибках
    // чтобы агент понимал текущее состояние
    if (errors.length > 0 && appliedFilters.length === 0) {
      return {
        success: false,
        error: errors.join('; '),
        results
      };
    }

    return {
      success: true,
      action: appliedFilters.length > 0 ? `applied filters: ${appliedFilters.join('; ')}` : 'no filters applied',
      error: errors.length > 0 ? errors.join('; ') : undefined,
      results
    };
  }

  /**
   * Просмотр автомобилей
   * Параметры: { offerId: string }
   */
  async viewCars({ offerId }) {
    console.log('[CommandExecutor] Executing view_cars with offerId:', offerId);

    // Валидация параметра
    if (!offerId || typeof offerId !== 'string') {
      console.error('[CommandExecutor] offerId is required');
      return {
        success: false,
        error: 'offerId is required (e.g., "534162-1")'
      };
    }

    // #982: Off-catalog → open the detail page directly in ONE navigation instead of the
    // old go_cars/go_website_page → view_cars 2-step. window.allCars isn't loaded off the
    // catalog SPA, so we skip the local existence check here — the catalog validates on load
    // (the offerId came from search_car_inventory, which is authoritative).
    const spaCheck = this._canUseSpaNavigation();
    if (!spaCheck.allowed) {
      console.log(`[CommandExecutor] view_cars off-catalog (page: ${spaCheck.currentPage}) — deep-linking to /cars/${offerId}`);
      return this._deepLinkToCatalog(`/cars/${encodeURIComponent(offerId)}`);
    }

    // Проверяем существование оффера в данных (используем глобальную переменную из App)
    const allCars = window.allCars || [];
    console.log('[CommandExecutor] Checking offer existence:', {
      offerId,
      allCarsLength: allCars.length,
      firstCarId: allCars[0]?.id,
      firstCarIdType: typeof allCars[0]?.id,
      searchingForType: typeof offerId
    });

    const carExists = allCars.find(car => car.id === offerId);

    if (!carExists) {
      // Оффер не существует - переходим на /cars и сообщаем об ошибке
      console.error('[CommandExecutor] Offer not found:', offerId);
      console.error('[CommandExecutor] Available car IDs sample:', allCars.slice(0, 5).map(c => c.id));
      window.location.hash = '#/cars';
      // Скролл наверх (оптимизированно - одинарный RAF)
      requestAnimationFrame(() => {
        scrollWindowToTop();
      });

      return {
        success: false,
        error: `Offer "${offerId}" does not exist. Showing all available cars instead.`
      };
    }

    // Сохраняем текущий hash для кнопки "назад" на странице детали
    try { sessionStorage.setItem('b2b-back-to-cars', window.location.hash.replace('#', '') || '/cars'); } catch (_) { /* ignore */ }

    // Сохраняем текущее состояние в навигационный стек для go_back (#734)
    this._pushHistory();

    // Оффер существует - переходим на страницу детализации
    window.location.hash = `#/cars/${offerId}`;
    // Скролл наверх (оптимизированно - одинарный RAF)
    requestAnimationFrame(() => {
      scrollWindowToTop();
    });

    console.log(`[CommandExecutor] Navigated to offer: ${offerId}`);
    return {
      success: true,
      action: `viewing offer ${offerId}`
    };
  }

  /**
   * Очистка фильтров
   * Все фильтры работают через URL params (DOM элементы не нужны)
   * Параметры: { filterTypes?: ['make', 'model', 'price', 'all'] }
   */
  async clearFilters({ filterTypes } = {}) {
    // Убедиться что мы на странице /cars
    const navigationResult = await this.ensureOnCarsPage();
    if (!navigationResult.success) {
      return navigationResult;
    }

    // Получаем текущие URL параметры
    const hash = window.location.hash;
    const [path, queryString] = hash.split('?');
    const params = new URLSearchParams(queryString || '');

    const clearedFilters = [];

    // Если filterTypes не указаны или содержат 'all', очищаем все фильтры
    const shouldClearAll = !filterTypes || filterTypes.includes('all');

    if (shouldClearAll) {
      // Удаляем все фильтр-параметры
      Object.entries(this.filterParamMap).forEach(([filterType, paramNames]) => {
        paramNames.forEach(param => params.delete(param));
        clearedFilters.push(filterType);
      });
      console.log('[CommandExecutor] Clearing all filters');
    } else {
      // Очищаем только указанные фильтры
      filterTypes.forEach(filterType => {
        const paramNames = this.filterParamMap[filterType];
        if (paramNames) {
          paramNames.forEach(param => params.delete(param));
          clearedFilters.push(filterType);
        } else {
          console.warn(`[CommandExecutor] Unknown filter type: ${filterType}`);
        }
      });
    }

    // Обновляем URL
    const newHash = params.toString() ? `${path}?${params.toString()}` : path;
    console.log(`[CommandExecutor] Cleared filters: ${clearedFilters.join(', ')}, new URL: ${newHash}`);
    window.location.hash = newHash;

    // Читаем результаты после очистки
    const results = await this.getFilteredResultsOptimized();
    return {
      success: true,
      clearedFilters,
      action: shouldClearAll ? 'cleared all filters' : `cleared filters: ${clearedFilters.join(', ')}`,
      results
    };
  }

  /**
   * Установка сортировки
   * Параметры: { sortBy: 'price-asc' | 'price-desc' | 'year-desc' | 'year-asc' | 'mileage-asc' | 'mileage-desc' }
   */
  setSort({ sortBy }) {
    if (!sortBy) {
      return { success: false, error: 'sortBy is required' };
    }

    // Получаем текущие параметры из URL
    const hash = window.location.hash;
    const [path, queryString] = hash.split('?');
    const params = new URLSearchParams(queryString || '');

    // Устанавливаем параметр sortBy
    params.set('sortBy', sortBy);

    // Обновляем URL
    const newHash = params.toString() ? `${path}?${params.toString()}` : path;
    console.log(`[CommandExecutor] Setting sortBy to: ${sortBy}`);
    window.location.hash = newHash;

    return { success: true, action: `set sort to ${sortBy}` };
  }

  /**
   * Toggle Available Only filter
   * Hides/shows sold/reserved cars (statusSold === 'Yes')
   * Параметры: { enabled: boolean }
   */
  async toggleAvailableOnly({ enabled }) {
    // Убедиться что мы на странице /cars
    const navigationResult = await this.ensureOnCarsPage();
    if (!navigationResult.success) return navigationResult;

    const hash = window.location.hash;
    const [path, queryString] = hash.split('?');
    const urlParams = new URLSearchParams(queryString || '');

    if (enabled) {
      urlParams.set('availableOnly', 'true');
    } else {
      urlParams.delete('availableOnly');
    }

    const newHash = urlParams.toString() ? `${path}?${urlParams.toString()}` : path;
    window.location.hash = newHash;

    const results = await this.getFilteredResultsOptimized();
    return {
      success: true,
      action: enabled ? 'showing only cars for sale (hiding sold/reserved)' : 'showing all cars including sold/reserved',
      results
    };
  }

  /**
   * Читаем отфильтрованные результаты из window
   * @returns {Object|null} Результаты фильтрации
   */
  getFilteredResults() {
    console.log('[CommandExecutor] getFilteredResults called');
    const filteredData = window.currentFilteredCars;

    // Issue #523: Всегда возвращаем объект с информацией о статусе
    // чтобы агент понимал что произошло с командой
    if (!filteredData || !Array.isArray(filteredData.cars)) {
      console.warn('[CommandExecutor] No filtered cars found in window.currentFilteredCars');
      return {
        status: 'pending',
        statusMessage: 'Filter applied but results are still loading. The filter was executed successfully.',
        total: 0,
        allCarsTotal: 0,
        showing: 0,
        cars: [],
        visibleOfferIds: [],
        priceRange: null,
        mileageRange: null
      };
    }

    const { cars, total, allCarsTotal } = filteredData;
    console.log(`[CommandExecutor] Reading filtered results: ${total} cars (showing ${Math.min(10, cars.length)})`);

    // Форматируем первые 10 машин для агента
    const showing = Math.min(10, cars.length);
    const formattedCars = cars.slice(0, showing).map(this.formatCarForAgent);

    // Вычисляем диапазоны
    const prices = cars.filter(c => c.price > 0).map(c => c.price);
    const mileages = cars.filter(c => c.mileage >= 0).map(c => c.mileage);

    const priceRange = prices.length > 0
      ? { min: Math.min(...prices), max: Math.max(...prices) }
      : null;

    const mileageRange = mileages.length > 0
      ? { min: Math.min(...mileages), max: Math.max(...mileages) }
      : null;

    return {
      status: 'ready',
      statusMessage: total > 0
        ? `Found ${total} cars matching the filter criteria.`
        : 'No cars match the current filter criteria.',
      total,
      allCarsTotal,
      showing,
      cars: formattedCars,
      visibleOfferIds: cars.map(c => c.id),  // Все видимые offer IDs для использования в view_cars
      priceRange,
      mileageRange
    };
  }

  /**
   * Issue #714: Parse all active filters from current URL params.
   * Used by setSingleFilter to get full filter context for diagnosis.
   */
  _parseActiveFilters(urlParams) {
    const filters = {};
    for (const filterType of this.categoricalFilters) {
      const [paramName] = this.filterParamMap[filterType];
      const val = urlParams.get(paramName);
      if (val) filters[filterType] = val.split(',');
    }
    for (const filterType of this.rangeFilters) {
      const [minParam, maxParam] = this.filterParamMap[filterType];
      const minVal = urlParams.get(minParam);
      const maxVal = urlParams.get(maxParam);
      if (minVal || maxVal) {
        filters[filterType] = {};
        if (minVal) filters[filterType].min = Number(minVal);
        if (maxVal) filters[filterType].max = Number(maxVal);
      }
    }
    return filters;
  }

  /**
   * Issue #714: Diagnose why set_filters returned 0 results.
   * Delegates to standalone diagnoseZeroResults() function.
   */
  _diagnoseZeroResults(filters) {
    const allCars = window.currentFilteredCars?.allCars;
    const result = diagnoseZeroResults(filters, allCars, this.categoricalFilters, this.rangeFilters);
    console.debug('[CommandExecutor] Zero-results diagnosis:', result);
    return result;
  }

  /**
   * Форматирование машины для агента
   * @param {Object} car - Объект машины
   * @returns {Object} Форматированная информация для агента
   */
  formatCarForAgent(car) {
    return {
      id: car.id,
      title: carTitle(car),
      price: car.price,
      mileage: car.mileage,
      year: car.year,
      make: car.make,
      model: car.model,
      bodyStyle: car.bodyStyle || 'N/A',
      fuelType: car.fuelType || 'N/A',
      transmission: car.transmission || 'N/A',
      statusSold: car.statusSold || null,
      // The canonical delivery cell ('immediate' / 'YYYY-MM' / none, #1488). The agent
      // shows it as the car's arrival; without it a list of cars in transit went to the
      // customer as "10 available SUV matches".
      deliveryDate: deliveryCell(car.deliveryDate)
    };
  }

  /**
   * Показать уведомление об успешном бронировании
   * Параметры: { date, time, name, phone, bookingId, carInfo? }
   */
  notifySuccessBooking({ date, time, name, phone, bookingId, carInfo }) {
    console.log('[CommandExecutor] Showing success booking notification:', {
      date,
      time,
      name,
      phone,
      bookingId,
      carInfo
    });

    // Валидация обязательных параметров
    if (!date || !time || !name || !phone || !bookingId) {
      return {
        success: false,
        error: 'Missing required parameters: date, time, name, phone, bookingId are required'
      };
    }

    // Проверяем наличие глобальной функции showNotification
    if (typeof window.showSuccessBookingNotification !== 'function') {
      console.error('[CommandExecutor] window.showSuccessBookingNotification is not defined');
      return {
        success: false,
        error: 'Notification system not initialized'
      };
    }

    // Вызываем глобальную функцию для показа уведомления
    window.showSuccessBookingNotification({
      date,
      time,
      name,
      phone,
      bookingId,
      carInfo
    });

    return {
      success: true,
      action: `shown booking notification for ${name} on ${date} at ${time}`
    };
  }

  /**
   * Отправка события агента после выполнения команды
   * @param {string} commandId - ID команды
   * @param {Object} params - Параметры команды
   * @param {Object} result - Результат выполнения команды
   */
  async sendAgentActionEvent(commandId, params, result) {
    try {
      // Ленивая загрузка browserControlWS для избежания циклических зависимостей
      if (!browserControlWS) {
        const module = await import('./browserControlWebSocket.js');
        browserControlWS = module.browserControlWS;
      }

      // Маппинг команд на типы событий
      const navigationCommands = [
        'go_home', 'go_cars', 'go_about', 'go_contact', 'go_back_cars', 'go_back',
        'go_book_test_drive', 'go_service', 'go_service_appointment'
      ];
      const filterCommands = ['set_filter', 'set_filters', 'clear_filters', 'set_sort', 'toggle_available_only'];
      const viewCommands = ['view_cars'];

      if (navigationCommands.includes(commandId)) {
        // Отправляем событие навигации
        const hash = window.location.hash;
        const route = hash.startsWith('#') ? hash.substring(1) : hash || '/';

        browserControlWS.sendAgentAction('navigation', {
          commandId,
          to: route,
          params,
        });
      } else if (filterCommands.includes(commandId)) {
        // Отправляем событие изменения фильтров
        const activeFilters = this.getActiveFilters();

        browserControlWS.sendAgentAction('filter-changed', {
          commandId,
          activeFilters,
          filteredCount: result.results?.total || 0,
          totalCount: result.results?.allCarsTotal || 0,
          params,
        });
      } else if (viewCommands.includes(commandId)) {
        // Отправляем событие просмотра автомобиля
        browserControlWS.sendAgentAction('car-view', {
          commandId,
          offerId: params.offerId,
          params,
        });
      }
      // Для остальных команд (scroll_top, scroll_bottom, notify_success_booking)
      // не отправляем user-action события, т.к. они не являются основными действиями пользователя
    } catch (error) {
      console.error('[CommandExecutor] Error sending agent action event:', error);
      // Не бросаем ошибку дальше, чтобы не прерывать выполнение команды
    }
  }

  /**
   * Получить активные фильтры из URL
   * @returns {Object} Активные фильтры
   */
  getActiveFilters() {
    const hash = window.location.hash;
    const [, queryString] = hash.split('?');
    const params = new URLSearchParams(queryString || '');

    const activeFilters = {};

    // Категориальные фильтры
    ['make', 'model', 'bodyStyle', 'fuelType', 'transmission'].forEach(filterType => {
      const value = params.get(filterType);
      if (value) {
        activeFilters[filterType] = value.split(',');
      }
    });

    // Range фильтры
    const rangeFilters = {
      price: { min: 'minPrice', max: 'maxPrice' },
      mileage: { min: 'minMileage', max: 'maxMileage' },
      year: { min: 'minYear', max: 'maxYear' }
    };

    Object.entries(rangeFilters).forEach(([filterType, paramNames]) => {
      const min = params.get(paramNames.min);
      const max = params.get(paramNames.max);

      if (min || max) {
        activeFilters[filterType] = {
          ...(min && { min: parseInt(min) }),
          ...(max && { max: parseInt(max) })
        };
      }
    });

    // Boolean toggle
    const availableOnly = params.get('availableOnly');
    if (availableOnly === 'true') {
      activeFilters.availableOnly = true;
    }

    // Сортировка
    const sortBy = params.get('sortBy');
    if (sortBy) {
      activeFilters.sortBy = sortBy;
    }

    return activeFilters;
  }

  /**
   * Показать виджет от агента
   * Добавляет сообщение с виджетом в чат
   *
   * Widget полностью сформирован бэкендом, фронтенд только рендерит
   *
   * @param {Object} params
   * @param {string} params.messageText - Текст сообщения (опционально)
   * @param {Object} params.widget - Объект виджета от бэкенда
   * @param {string} params.widget.type - Тип виджета ('form', 'youtube', 'cards', 'gallery', 'comparison')
   * @param {string} params.widget.id - Уникальный ID виджета
   * @param {Object} params.widget.data - Данные виджета (структура зависит от типа)
   * @returns {Promise<Object>} Результат операции
   */
  async showWidget({ messageText = '', widget } = {}) {
    console.log(`[CommandExecutor] Showing widget:`, widget);

    // #1411: off by default — nothing emits show_widget on purpose, and the agent reached for
    // it to put a car on screen. Refuse with a pointer to the commands that do that.
    if (!config.ENABLE_SHOW_WIDGET) {
      console.warn('[CommandExecutor] show_widget is disabled (ENABLE_SHOW_WIDGET is off)');
      return {
        success: false,
        error: 'show_widget is disabled on this site. To put one car on screen use view_cars; to show a selection use set_filters.'
      };
    }

    // Проверка что виджет передан
    if (!widget || !widget.type || !widget.id) {
      console.error('[CommandExecutor] Invalid widget object:', widget);
      return {
        success: false,
        error: 'Invalid widget: missing type or id'
      };
    }

    // #1411: WidgetRenderer has nothing for an unknown type — that is not "displayed".
    if (!SUPPORTED_WIDGET_TYPES.includes(widget.type)) {
      console.error('[CommandExecutor] Unsupported widget type:', widget.type);
      return {
        success: false,
        error: `Unknown widget type "${widget.type}". Supported types: ${SUPPORTED_WIDGET_TYPES.join(', ')}.`
      };
    }

    // Проверка что callback установлен
    if (!this.onAddMessage) {
      console.error('[CommandExecutor] onAddMessage callback not set. Widget cannot be displayed.');
      return {
        success: false,
        error: 'onAddMessage callback not configured'
      };
    }

    try {
      // Создаем сообщение с виджетом
      const message = {
        id: Date.now(),
        role: 'assistant',
        text: messageText,
        timestamp: new Date(),
        widget: widget // widget уже полностью сформирован бэкендом
      };

      // Добавляем сообщение через callback
      this.onAddMessage(message);

      console.log(`[CommandExecutor] Widget displayed successfully: ${widget.type} (${widget.id})`);

      return {
        success: true,
        action: `displayed ${widget.type} widget`,
        widgetId: widget.id,
        displayed: true
      };
    } catch (error) {
      console.error('[CommandExecutor] Error displaying widget:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
}

// Singleton экземпляр
export const commandExecutor = new CommandExecutor();

export default commandExecutor;
